/* eslint-disable @typescript-eslint/no-explicit-any */

import { filter, find, map, replace, trim } from 'es-toolkit/compat';

import { alertSuspiciousContentInNotes } from '@tock/logging';
import { core } from '@tock/coretypes';

const defaultFormat = /(\d{1,4})/g;
const amexFormat = /(\d{1,4})(\d{1,6})?(\d{1,5})?/;

// This should be kept to match the Regex in CreditCardInStringTester.java in core
const CREDIT_CARD_REGEX =
  /(?:[^0-9]|^)([4-5](?:[^0-9]?[0-9]){3}(?:(?:[^0-9]?[0-9]){4}){3}|[3](?:[^0-9]?[0-9]){3}(?:[^0-9]?[0-9]){6}(?:[^0-9]?[0-9]){5})/g;

interface Card {
  type: string;
  pattern: RegExp;
  format: RegExp;
  minLength: number;
  maxLength: number;
  cvcLength: number[];
}

export const cards: Card[] = [
  {
    // MAESTRO
    type: 'MasterCard',
    pattern: /^(5018|5020|5038|6304|6759|676[1-3]|6768|5612|5893|6304|6759|0604|6390)/,
    format: defaultFormat,
    minLength: 12,
    maxLength: 19,
    cvcLength: [3],
  },
  {
    type: 'Diners Club',
    pattern: /^(36|38|30[0-5])/,
    format: defaultFormat,
    minLength: 14,
    maxLength: 14,
    cvcLength: [3],
  },
  {
    type: 'JCB',
    pattern: /^35/,
    format: defaultFormat,
    minLength: 16,
    maxLength: 16,
    cvcLength: [3],
  },
  {
    type: 'Discover',
    pattern: /^(6011|65|64[4-9]|622)/,
    format: defaultFormat,
    minLength: 16,
    maxLength: 16,
    cvcLength: [3],
  },
  {
    type: 'MasterCard',
    pattern: /^5[1-5]/,
    format: defaultFormat,
    minLength: 16,
    maxLength: 16,
    cvcLength: [3],
  },
  {
    type: 'American Express',
    pattern: /^3[47]/,
    format: amexFormat,
    minLength: 15,
    maxLength: 15,
    cvcLength: [3, 4],
  },
  {
    type: 'Visa',
    pattern: /^4/,
    format: defaultFormat,
    minLength: 13,
    maxLength: 16,
    cvcLength: [3],
  },
  {
    type: 'Unknown',
    pattern: /^/,
    format: defaultFormat,
    minLength: 13,
    maxLength: 16,
    cvcLength: [3],
  },
];

export function cardNumber(value: any) {
  //eslint-disable-next-line no-useless-escape
  return (value + '').replace(/[_\ ]/g, '');
}

export function cardFromNumber(value: any): Card | undefined {
  const num = cardNumber(value);
  return find(cards, (card: Card) => card.pattern.test(num));
}

export function containsPotentialCardNumbers(str = ''): boolean {
  const strWithoutSpacesOrSomePunc = replace(str, /_|\.|-/g, '');
  let potentialCardStrings = map(strWithoutSpacesOrSomePunc.match(CREDIT_CARD_REGEX), trim);
  potentialCardStrings = map(potentialCardStrings, (card) => {
    return card.replace(/\D/g, '');
  });
  const isValid =
    filter(potentialCardStrings, (cardNumber) => {
      return isValidLuhn(cardNumber);
    }).length > 0;
  return isValid;
}

/**
 * Valid cards follow a check sum pattern to help prevent typos.
 */
export function isValidLuhn(ccNumber: string) {
  let sum = 0;
  let alternate = false;
  for (let i = ccNumber.length - 1; i >= 0; i--) {
    let n = parseInt(ccNumber[i]);
    if (alternate) {
      n *= 2;
      if (n > 9) {
        n = (n % 10) + 1;
      }
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

export function warnAboutPotentialCardNumbersInNote(
  str = '',
  initialValue?: string
): string | undefined {
  if (containsPotentialCardNumbers(str)) {
    if (containsPotentialCardNumbers(initialValue)) {
      alertSuspiciousContentInNotes('Suspicious notes changes prevented');
    } else {
      alertSuspiciousContentInNotes('Suspicious notes prevented for new notes');
    }
    return `Entering credit card information is not allowed.
     Please remove it to save these notes.`;
  }
  return;
}

export function addCvvToken(card: core.IPaymentCard): Promise<core.IPaymentCard> {
  return new Promise((resolve, reject) => {
    if (!window.braintreeHostedField) {
      reject();
    }

    window.braintreeHostedField
      .tokenize({ fieldsToTokenize: ['cvv'] })
      .then((token: { nonce: string }) => {
        resolve({
          ...card,
          cvvNonceToken: token.nonce,
        });
      });
  });
}
